import {ApartmentViewer} from "features/apartmentViewer/ApartmentViewer/ApartmentViewer";
import {PanoramaManager} from "features/apartmentViewer/ApartmentViewer/services/PanoramaManager";
import {ThumbNailModel} from "features/apartmentViewer/ApartmentViewer/models/ThumbNailModel";
import {FurnishingModel} from "features/apartmentViewer/ApartmentViewer/models/FurnishingModel";
import {MarkerService} from "features/apartmentViewer/ApartmentViewer/containers/MarkerService";
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";
import {PostProcessingRenderer} from "features/apartmentViewer/ApartmentViewer/renderers/PostProcessingRenderer";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";

export class ApartmentViewerFacade {
    private _hasStarted: boolean = false;

    constructor(
        private apartmentViewer: ApartmentViewer,
        private siteModel: SiteModel,
        private postProcessingRenderer: PostProcessingRenderer,
        private panoramaManager: PanoramaManager,
        private markerService: MarkerService,
        private canvasService: CanvasService,
        private settings: ApartmentViewerSettings,
        private cameraService: CameraHandler,
    ) {}

    async goToCamera(thumb: ThumbNailModel) {

        try {
            const marker = this.markerService.getMarkerById(thumb.id);

            if (marker) {
                await this.panoramaManager.moveToNewCamera(marker, false);
            }
        } catch (e) {
            console.error('Error when trying to go to camera:', e);
            throw e;
        }
    }

    async setNewFurnishing(furnishing: FurnishingModel) {
        try {
            this.siteModel.currentFurnishing = furnishing.id;
            await this.reloadCurrentPanorama();
        } catch (e) {
            console.error('Could not set new furnishing:', e);
            throw e;
        }
    }

    async reloadCurrentPanorama() {
        try {
            await this.panoramaManager.reloadCurrentPanorama();
        } catch (e) {
            console.error('Could not reload current panorama:', e);
            throw e;
        }
    }

    handleWindowResize() {
        if(!this._hasStarted) return;

        this.canvasService.height = window.innerHeight;
        this.canvasService.width = (16 / 9) * this.canvasService.height;

        this.postProcessingRenderer.setSize();
    }

    async startViewer(cameraRotationChange: (rotation: number) => void) {
        try {
            if (!this.siteModel || !this.markerService.hasMarkers()) return;

            const selectedMarker = this.markerService.getFirstMarker();

            if(!selectedMarker) return;

            await this.panoramaManager.loadAndSetPanorama(selectedMarker, 0, false);
            this._hasStarted = true;

            this.cameraService.onCameraRotationChange = cameraRotationChange;

            this.apartmentViewer.render();
        } catch (e) {
            console.log('Error when trying to start apartment viewer.', e);
            throw e;
        }
    }

    stopViewer() {
        try {
            this.apartmentViewer.dispose();
            this._hasStarted = false;
        } catch (e) {
            console.error('Could not stop apartment viewer:', e);
        }
    }

    async changeMobileState(isMobile: boolean) {
        try {
            this.settings.isMobile = isMobile;
            await this.reloadCurrentPanorama();
        } catch (e) {
            console.error('Could not change mobile state:', e);
            throw e;
        }
    }

    shouldUpdateMobileState(isMobile: boolean) {
        return this.settings.isMobile !== isMobile;
    }

    setLoadingState(value: boolean) {
        this.settings.loading = value;
    }

    setActiveMarkerCallback(callback: (isActive: boolean) => void) {
        this.settings.onActiveMarkerChange = callback;
    }



    getSettings() {
        return this.settings;
    }

    get hasStarted(): boolean {
        return this._hasStarted;
    }
}
